/* Copyright (c) 2024-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy, Optional, Self } from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { fromEvent, Observable, race, Subject, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { consumeKeydownUntilKeyup } from 'utils/lib/web-events/keyboard-event';
import { easeOutCubic } from '../scrolling/easing-function';
import { ScrollPlayer } from '../scrolling/scroll-player';
import { getScrollContainer, getScrollSnapBoundingRect, getScrollSnapTarget } from '../scrolling/scrolling';
import { FocusManager } from '../services/focus-manager';
import { getKeyFromDirection, isHorizontalDir, isVerticalDir } from '../services/spatial-navigation/spatial-navigation';
import {
    NavigationEvent,
    SpatialNavigationDirection
} from '../services/spatial-navigation/spatial-navigation-polyfill';
import { SpatialNavigationService } from '../services/spatial-navigation/spatial-navigation.service';
import { ContinuousNavigationMotionDirective } from './continuous-navigation-motion.directive';

export type Orientation = 'horizontal' | 'vertical';
export type AlignmentPolicy = 'always' | 'default';
enum ScrollDirection {
    Start,
    End
}
interface ScrollDestination {
    readonly x: number;
    readonly y: number;
    readonly targetRect: DOMRect;
    readonly keylineCoordinate: number;
}
interface ScrollSnapNavigation {
    readonly direction: SpatialNavigationDirection;
    readonly target: HTMLElement;
    readonly destination?: ScrollDestination;
}
/**
 * the block index should be a number in general. it is also possible to specify
 * the block index with `first` and `last` and let the directive to compute the
 * actual index when navigation happens.
 *
 * `first` is the index of the first block that is always equal to 0.
 * `last` is the index of the last block that is fully inside the scrollport.
 */
export type BlockIndex = number | 'first' | 'last';
const ScrollDuration = 200;
const IntersectionObserverThreshold = 0.95;

/**
 * A directive that forces alignment when moving focus in the installed scroll container
 *
 */
@Directive({
    selector: '[nvSpatialNavigationScrollSnap]',
    standalone: true
})
export class SpatialNavigationScrollSnapDirective implements OnDestroy, AfterViewInit {
    /**
     * Whether the directive is enabled
     */
    @Input() get enableScrollSnap(): boolean {
        return this._enableScrollSnap;
    }
    set enableScrollSnap(value: unknown) {
        this._enableScrollSnap = coerceBooleanProperty(value);
    }
    private _enableScrollSnap = false;

    /**
     * The direction that the scroller scrolls
     *
     * It is either `horizontal` or `vertical`.
     */
    @Input() orientation: Orientation = 'horizontal';

    /**
     * The pixel size of the padding area between the beginning edge of the scrollport and the
     * first block of this scroll container.
     */
    @Input() containerPadding = 0;

    /**
     * The block size of this scroll container.
     */
    @Input() blockSize = 0;

    /**
     * The pixel size of the padding area between the block's anchor edge and the focused item's snap edge
     */
    @Input() blockPadding = 0;

    /**
     * The index of the block that a focused item must snap to if the scroll direction is
     * at the start direction of the scroll container. `blockIndexStart` should be a number by default.
     * if specified with `first` and `last`, the actual block index will be calculated when navigation happens.
     */
    @Input() blockIndexStart: BlockIndex = 'first';

    /**
     * The index of the block that a focused item must snap to if the scroll direction is
     * at the end direction of the scroll container. `blockIndexEnd` should be a number by default.
     * if specified with `first` and `last`, the actual block index will be calculated when navigation happens.
     */
    @Input() blockIndexEnd: BlockIndex = 'last';

    /**
     * The codition to align the focused item
     *
     * `default` aligns the focused item if it is out of the viewport.
     * `always` aligns the focused item on each navigation.
     */
    @Input() alignmentPolicy: AlignmentPolicy = 'default';
    @Input() isRTL = false;

    private destroyed$ = new Subject();

    get scrollerElement() {
        if (this.cdkVirtualScrollViewport) {
            return this.cdkVirtualScrollViewport.getElementRef().nativeElement;
        }

        // retrieve the nearest scroller element if the directive cannot retrieve it in afterviewinit hook
        // or it is somehow disconnected from DOM
        if (!this._scrollerElement?.isConnected) {
            this._scrollerElement = getScrollContainer(this.elementRef.nativeElement);
        }

        return this._scrollerElement;
    }
    private _scrollerElement: HTMLElement;

    private scrollingSubscription: Subscription;
    private get isScrolling() {
        return !!this.scrollingSubscription && !this.scrollingSubscription?.closed;
    }
    private keyRepeatSubscription: Subscription;

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        private ngZone: NgZone,
        private focusManager: FocusManager,
        private spatialNavigationService: SpatialNavigationService,
        @Optional() @Self() private continuousNavigationMotion: ContinuousNavigationMotionDirective | null,
        @Optional() private cdkVirtualScrollViewport: CdkVirtualScrollViewport
    ) {}

    ngAfterViewInit(): void {
        if (!this.spatialNavigationService.enabled) {
            return;
        }

        this.refreshScrollerElement();

        this.ngZone.runOutsideAngular(() => {
            fromEvent<NavigationEvent>(this.elementRef.nativeElement, 'navbeforefocus')
                .pipe(takeUntil(this.destroyed$))
                .subscribe(this.navBeforeFocus);
        });
    }

    refreshScrollerElement() {
        if (!this.cdkVirtualScrollViewport) {
            this._scrollerElement = getScrollContainer(this.elementRef.nativeElement);
        }
    }

    private isValidOrientation(event: NavigationEvent): boolean {
        return this.orientation === 'horizontal' ? isHorizontalDir(event.detail.dir) : isVerticalDir(event.detail.dir);
    }

    private readonly navBeforeFocus = (event: NavigationEvent): void => {
        const motionEnabled = !!this.continuousNavigationMotion?.enabled;
        const navigation = this.prepareNavigation(event, motionEnabled);
        if (!navigation) {
            return;
        }

        if (motionEnabled) {
            event.preventDefault();
            event.stopPropagation();
            this.cancelLegacyNavigation();
            this.continuousNavigationMotion!.start({
                direction: navigation.direction,
                scroller: this.scrollerElement,
                destination: navigation.destination,
                canContinue: () => this.enableScrollSnap
            });
            this.focusManager.focusViaLastOrigin(navigation.target, { preventScroll: true });
            return;
        }

        if (!navigation.destination) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        const { x, y, targetRect, keylineCoordinate } = navigation.destination;
        this.cancelLegacyNavigation();

        const scrollPlayer = new ScrollPlayer(this.scrollerElement, x, y, {
            easingFunction: easeOutCubic,
            duration: ScrollDuration
        });
        const detectedScrollEnd$ = this.detectScrollEnd(
            navigation.direction,
            getScrollSnapTarget(navigation.target) as HTMLElement,
            targetRect,
            keylineCoordinate
        );
        // Release repeats when either native geometry or ScrollPlayer reports completion.
        const repeatRelease$ = race(detectedScrollEnd$, scrollPlayer.onScrollEnd(), this.destroyed$);

        // swallow key repeat during scrolling
        this.keyRepeatSubscription = consumeKeydownUntilKeyup(window, getKeyFromDirection(navigation.direction))
            .pipe(
                takeUntil(repeatRelease$),
                finalize(() => {
                    this.keyRepeatSubscription = undefined;
                })
            )
            .subscribe();

        this.scrollingSubscription = scrollPlayer
            .start()
            .pipe(
                finalize(() => {
                    this.scrollingSubscription = undefined;
                }),
                takeUntil(this.destroyed$)
            )
            .subscribe();

        this.focusManager.focusViaLastOrigin(navigation.target, { preventScroll: true });
    };

    private prepareNavigation(event: NavigationEvent, motionEnabled: boolean): ScrollSnapNavigation | undefined {
        const target = event.target;
        if (
            !this.enableScrollSnap ||
            event.defaultPrevented ||
            !this.isValidOrientation(event) ||
            !this.scrollerElement ||
            !(target instanceof HTMLElement) ||
            (motionEnabled && target === this.elementRef.nativeElement) ||
            !this.elementRef.nativeElement.contains(target)
        ) {
            return undefined;
        }

        return {
            direction: event.detail.dir,
            target,
            destination: this.shouldScroll(event) ? this.getScrollDestination(event) : undefined
        };
    }

    private getScrollDestination(event: NavigationEvent): ScrollDestination {
        let scrollDirection: ScrollDirection;
        if (this.orientation === 'horizontal') {
            if (this.isRTL) {
                scrollDirection = event.detail.dir === 'left' ? ScrollDirection.End : ScrollDirection.Start;
            } else {
                scrollDirection = event.detail.dir === 'left' ? ScrollDirection.Start : ScrollDirection.End;
            }
        } else {
            scrollDirection = event.detail.dir === 'up' ? ScrollDirection.Start : ScrollDirection.End;
        }

        const keylineCoordinate = this.getKeylineCoordinateOf(scrollDirection);
        const targetRect = getScrollSnapBoundingRect(event.target as HTMLElement);
        let x = this.scrollerElement.scrollLeft;
        let y = this.scrollerElement.scrollTop;
        if (this.orientation === 'horizontal') {
            const snapEdgeCoordinate = this.isRTL ? targetRect.right : targetRect.left;
            x += snapEdgeCoordinate - keylineCoordinate;
        } else {
            y += targetRect.y - keylineCoordinate;
        }

        return { x, y, targetRect, keylineCoordinate };
    }

    private cancelLegacyNavigation(): void {
        if (this.isScrolling) {
            this.scrollingSubscription.unsubscribe();
        }
        this.scrollingSubscription = undefined;

        if (this.keyRepeatSubscription && !this.keyRepeatSubscription.closed) {
            this.keyRepeatSubscription.unsubscribe();
        }
        this.keyRepeatSubscription = undefined;
    }

    private detectScrollEnd(
        direction: SpatialNavigationDirection,
        targetElement: HTMLElement,
        targetRect: DOMRect,
        keylineCoordinate: number
    ): Observable<void> {
        const scrollerRect = this.scrollerElement.getBoundingClientRect();
        return new Observable(subscriber => {
            let rootMargin = '';
            let margin: number = 0;
            switch (direction) {
                case 'down':
                    margin = scrollerRect.height - (keylineCoordinate + targetRect.height);
                    rootMargin = `0px 0px ${-margin}px 0px`;
                    break;
                case 'left':
                    margin = this.isRTL ? keylineCoordinate - targetRect.width : keylineCoordinate;
                    rootMargin = `0px 0px 0px ${-margin}px`;
                    break;
                case 'right':
                    margin = this.isRTL
                        ? scrollerRect.width - keylineCoordinate
                        : scrollerRect.width - (keylineCoordinate + targetRect.width);
                    rootMargin = `0px ${-margin}px 0px 0px`;
                    break;
                case 'up':
                    margin = keylineCoordinate;
                    rootMargin = `${-margin}px 0px 0px 0px`;
                    break;
            }

            const intersectionObserver = new IntersectionObserver(
                entries => {
                    for (let i = 0; i < entries.length; i++) {
                        if (entries[i].intersectionRatio >= IntersectionObserverThreshold) {
                            intersectionObserver.disconnect();
                            subscriber.next();
                            subscriber.complete();
                            break;
                        }
                    }
                },
                {
                    root: this.scrollerElement,
                    rootMargin,
                    threshold: [IntersectionObserverThreshold]
                }
            );

            intersectionObserver.observe(targetElement as HTMLElement);

            return () => {
                intersectionObserver.disconnect();
            };
        });
    }

    /**
     * Convert the block index into number index
     */
    private getIndex(
        blockIndex: BlockIndex,
        scrollerRect: DOMRect,
        orientation: Orientation,
        containerPadding: number,
        blockSize: number
    ): number {
        if (blockSize <= 0) {
            throw new Error(
                `block size should be greater than 0.
                current block size is ${blockSize},
                window.innerHeight = ${window.innerHeight},
                document.documentElement.clientHeight = ${document.documentElement.clientHeight}`
            );
        }
        if (typeof blockIndex == 'number') {
            return blockIndex;
        } else if (blockIndex === 'first') {
            return 0;
        } else if (blockIndex === 'last') {
            const scrollportSize = orientation === 'horizontal' ? scrollerRect.width : scrollerRect.height;
            // if the scroll port's size minus container padding is less than the block size, just return 0
            if (scrollportSize - containerPadding <= blockSize) {
                return 0;
            }
            // minus one as the block index is zero-indexed
            return Math.floor((scrollportSize - containerPadding) / blockSize) - 1;
        }
        return 0;
    }

    /**
     * Get keyline coordiante of a scroll direction.
     *
     * return keyline's x coordinate if the scroll direction is horizontal
     * return keyline's y coordinate if the scroll direction is vertical
     */
    private getKeylineCoordinateOf(direction: ScrollDirection): number {
        const scrollerRect = this.scrollerElement.getBoundingClientRect();
        const keyBlock = direction === ScrollDirection.Start ? this.blockIndexStart : this.blockIndexEnd;
        const keylineCoordinate = this.computeKeyLineCoordinate(
            keyBlock,
            scrollerRect,
            this.orientation,
            this.blockSize,
            this.containerPadding,
            this.blockPadding
        );
        if (this.orientation === 'horizontal' && this.isRTL) {
            return scrollerRect.width - keylineCoordinate;
        }
        return keylineCoordinate;
    }

    /**
     * Compute the keyline's coordinate from the index of the key block
     *
     * @param keyBlock the index of the key block
     * @param scrollerRect the bounding rectangle of the scrolling container
     * @param orientation if the orientation is vertical, the returned value is the x coordinate. if the orientation is horizontal,
     * the returned value is the y coordinate
     * @param blockSize the size of a block in this scroll container
     * @param containerPadding the size of reserved space between the first block and the beginning edge of the container
     * @param blockPadding the size of the reserved space between a block's start edge and the focused item
     * @returns the one dimension coordinate of the keyline
     */
    private computeKeyLineCoordinate(
        keyBlock: BlockIndex,
        scrollerRect: DOMRect,
        orientation: Orientation,
        blockSize: number,
        containerPadding: number,
        blockPadding: number
    ): number {
        const beginningEdgeCoordinate = orientation === 'horizontal' ? scrollerRect.x : scrollerRect.y;
        return (
            beginningEdgeCoordinate +
            containerPadding +
            blockSize * this.getIndex(keyBlock, scrollerRect, orientation, containerPadding, blockSize) +
            blockPadding
        );
    }

    private shouldScroll(e: NavigationEvent) {
        if (this.alignmentPolicy === 'always') {
            return true;
        }
        return this.outOfViewport(e.detail.dir, e.target as HTMLElement);
    }

    private outOfViewport(dir: SpatialNavigationDirection, element: HTMLElement) {
        const elementRect = element.getBoundingClientRect();
        const scrollerRect = this.scrollerElement.getBoundingClientRect();

        switch (dir) {
            case 'up':
                return elementRect.top < scrollerRect.top;
            case 'down':
                return elementRect.bottom > scrollerRect.bottom;
            case 'left':
                return elementRect.left < scrollerRect.left;
            case 'right':
                return elementRect.right > scrollerRect.right;
            default:
                throw new Error(`${dir} is not a valid spatial navigation direction.`);
        }
    }

    /**
     * Compute necessary space to align the last focusable item to the scroll container's key line
     *
     * @param lastItemSize the size of the last focusable item
     * @param options.scrollerRect the bounding rectangle of the scrolling container
     * @param options.orientation the orientation of the scroll container
     * @param options.blockSize the size of a block in this scroll container
     * @param options.containerPadding the size of reserved space between the first block and the beginning edge of the container
     * @param options.blockPadding the size of the reserved space between a block's start edge and the focused item
     * @returns the one dimension coordinate of the keyline
     */
    getAlignmentBlockSize(
        lastItemSize: number,
        options?: {
            orientation?: Orientation;
            keyBlock?: BlockIndex;
            blockSize?: number;
            containerPadding?: number;
            blockPadding?: number;
        }
    ): number {
        if (!this.scrollerElement) {
            return 0;
        }
        const scrollerRect = this.scrollerElement.getBoundingClientRect();
        const scrollportSize = this.orientation === 'horizontal' ? scrollerRect.width : scrollerRect.height;
        const blockIndex = options?.keyBlock ?? this.blockIndexStart;
        const blockSize = options?.blockSize ?? this.blockSize;
        const containerPadding = options?.containerPadding ?? this.containerPadding;
        const blockPadding = options?.blockPadding ?? this.blockPadding;
        const orientation = options?.orientation ?? this.orientation;
        const keylineCoordinate = this.computeKeyLineCoordinate(
            blockIndex,
            scrollerRect,
            orientation,
            blockSize,
            containerPadding,
            blockPadding
        );
        return scrollportSize - keylineCoordinate - lastItemSize + blockPadding;
    }

    ngOnDestroy(): void {
        this.continuousNavigationMotion?.cancel();
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}
